import React, { useState } from "react";
import { TbLayoutBottombarCollapse, TbLayoutSidebarRightCollapse } from "react-icons/tb";
import type { AccuratePostingDetailItem } from "../../types/accuratePosting";

interface SerialRowTableProps {
  detailItems: AccuratePostingDetailItem[];
  onDetailItemsChange: (detailItems: AccuratePostingDetailItem[]) => void;
  onGenerateItem?: (detailIndex: number) => void;
  onGenerateRow?: (detailIndex: number, serialIndex: number) => void;
  isGenerating?: boolean;
}

const ROW_HEIGHT = 73;
const VIEWPORT_HEIGHT = 448;
const BATCH_SCROLL_ROW_LIMIT = 5;
const OVERSCAN_ROWS = 5;

const getVisibleRange = (rowCount: number, scrollTop: number, viewportHeight: number) => {
  const firstVisibleRow = Math.floor(scrollTop / ROW_HEIGHT);
  const start = Math.max(0, firstVisibleRow - OVERSCAN_ROWS);
  const visibleRowCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2;

  return {
    start,
    end: Math.min(rowCount, start + visibleRowCount),
  };
};

const SerialRowTable: React.FC<SerialRowTableProps> = ({
  detailItems,
  onDetailItemsChange,
  onGenerateItem,
  onGenerateRow,
  isGenerating = false,
}) => {
  const [scrollTopByDetail, setScrollTopByDetail] = useState<Record<number, number>>({});
  const [collapsedByDetail, setCollapsedByDetail] = useState<Record<string, boolean>>({});

  return (
    <div className="space-y-6">
      {detailItems.map((item, detailIndex) => {
        const detailKey = String(item.id ?? detailIndex);
        const isCollapsed = Boolean(collapsedByDetail[detailKey]);
        const scrollTop = scrollTopByDetail[detailIndex] || 0;
        const isBatch = item.serialNumberType === "BATCH";
        const shouldVirtualize = !isBatch || item.detailSerialNumber.length > BATCH_SCROLL_ROW_LIMIT;
        const viewportHeight = isBatch
          ? BATCH_SCROLL_ROW_LIMIT * ROW_HEIGHT
          : VIEWPORT_HEIGHT;
        const { start, end } = shouldVirtualize
          ? getVisibleRange(item.detailSerialNumber.length, scrollTop, viewportHeight)
          : { start: 0, end: item.detailSerialNumber.length };
        const visibleRows = item.detailSerialNumber.slice(start, end);
        const unique = item.serialNumberType === "UNIQUE";
        const columnCount = item.serialNumberType === "BATCH" ? 3 : 2;

        return (
          <section key={String(item.id ?? detailIndex)} className="overflow-x-auto rounded border">
            <div className="flex min-w-[30rem] items-center justify-between bg-gray-50 px-4 py-3">
              <div>
                <h2 className="font-semibold text-gray-800">{item.itemNo || "Item"}</h2>
                <span className="text-sm text-gray-600">
                  Quantity: {item.quantity ?? "-"} | {item.serialNumberType || "Serial"}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {onGenerateItem && item.manageSN !== false && (
                  <button
                    type="button"
                    className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                    disabled={isGenerating}
                    onClick={() => onGenerateItem(detailIndex)}
                  >
                    Generate for Item
                  </button>
                )}
                {item.serialNumberType === "BATCH" && (
                  <button
                    type="button"
                    className="rounded border border-blue-300 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50"
                    onClick={() =>
                      onDetailItemsChange(
                        detailItems.map((detail, index) =>
                          index === detailIndex
                            ? {
                                ...detail,
                                detailSerialNumber: [
                                  ...detail.detailSerialNumber,
                                  { serialNumberNo: "", quantity: 0 },
                                ],
                              }
                            : detail,
                        ),
                      )
                    }
                  >
                    Add row
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded border border-gray-400 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100"
                  aria-expanded={!isCollapsed}
                  aria-controls={`serial-detail-${detailKey}`}
                  aria-label={`${isCollapsed ? "Buka" : "Tutup"} item ${item.itemNo || `Item ${detailIndex + 1}`}`}
                  title={`${isCollapsed ? "Buka" : "Tutup"} item`}
                  onClick={() => setCollapsedByDetail((current) => ({
                    ...current,
                    [detailKey]: !isCollapsed,
                  }))}
                >
                  {isCollapsed
                    ? <TbLayoutSidebarRightCollapse aria-hidden="true" size={20} />
                    : <TbLayoutBottombarCollapse aria-hidden="true" size={20} />}
                </button>
              </div>
            </div>
            {!isCollapsed && <div
              id={`serial-detail-${detailKey}`}
              className="min-w-[30rem] overflow-auto"
              data-testid={`serial-scroll-${detailIndex}`}
              style={shouldVirtualize ? { maxHeight: viewportHeight } : undefined}
              onScroll={(event) => {
                const nextScrollTop = event.currentTarget.scrollTop;
                setScrollTopByDetail((current) => ({
                  ...current,
                  [detailIndex]: nextScrollTop,
                }));
              }}
            >
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 border-t bg-white text-gray-600">
                  <tr>
                    <th className="px-4 py-2" scope="col">Serial number</th>
                    <th className="px-4 py-2" scope="col">Quantity</th>
                    {item.serialNumberType === "BATCH" && <th scope="col" />}
                  </tr>
                </thead>
                <tbody>
                  {start > 0 && (
                    <tr aria-hidden="true" style={{ height: start * ROW_HEIGHT }}>
                      <td colSpan={columnCount} />
                    </tr>
                  )}
                  {visibleRows.map((row, visibleIndex) => {
                    const serialIndex = start + visibleIndex;
                    return (
                      <tr key={serialIndex} className="border-t" style={{ height: ROW_HEIGHT }}>
                        <td className="px-4 py-2">
                          <input
                            aria-label={`Serial number ${detailIndex + 1}-${serialIndex + 1}`}
                            data-testid={`serial-number-${detailIndex}-${serialIndex}`}
                            className="w-full rounded border border-gray-300 p-2"
                            value={row.serialNumberNo}
                            disabled={Boolean(row.isExisting)}
                            onChange={(event) =>
                              onDetailItemsChange(
                                detailItems.map((detail, itemIndex) =>
                                  itemIndex === detailIndex
                                    ? {
                                        ...detail,
                                        detailSerialNumber: detail.detailSerialNumber.map(
                                          (serial, rowIndex) =>
                                            rowIndex === serialIndex
                                              ? { ...serial, serialNumberNo: event.target.value }
                                              : serial,
                                        ),
                                      }
                                    : detail,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            aria-label={`Quantity ${detailIndex + 1}-${serialIndex + 1}`}
                            data-testid={`serial-quantity-${detailIndex}-${serialIndex}`}
                            className="w-32 rounded border border-gray-300 p-2 disabled:bg-gray-100"
                            type="number"
                            min="1"
                            value={unique ? 1 : row.quantity}
                            readOnly={unique}
                            disabled={unique}
                            onChange={(event) =>
                              onDetailItemsChange(
                                detailItems.map((detail, itemIndex) =>
                                  itemIndex === detailIndex
                                    ? {
                                        ...detail,
                                        detailSerialNumber: detail.detailSerialNumber.map(
                                          (serial, rowIndex) =>
                                            rowIndex === serialIndex
                                              ? {
                                                  ...serial,
                                                  quantity:
                                                    event.target.value === ""
                                                      ? 0
                                                      : Number(event.target.value),
                                                }
                                              : serial,
                                        ),
                                      }
                                    : detail,
                                ),
                              )
                            }
                          />
                        </td>
                          {item.serialNumberType === "BATCH" && (
                          <td className="px-4 py-2">
                            <div className="flex flex-wrap gap-2">
                              {onGenerateRow && item.manageSN !== false && !row.serialNumberNo.trim() && (
                                <button
                                  type="button"
                                  aria-label={`Generate Row ${serialIndex + 1}`}
                                  className="rounded border border-blue-300 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-400"
                                  disabled={isGenerating}
                                  onClick={() => onGenerateRow(detailIndex, serialIndex)}
                                >
                                  Generate
                                </button>
                              )}
                              <button
                                type="button"
                                aria-label={`Remove row ${serialIndex + 1}`}
                                className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                                onClick={() =>
                                  onDetailItemsChange(
                                    detailItems.map((detail, itemIndex) =>
                                      itemIndex === detailIndex
                                        ? {
                                            ...detail,
                                            detailSerialNumber: detail.detailSerialNumber.filter(
                                              (_, rowIndex) => rowIndex !== serialIndex,
                                            ),
                                          }
                                        : detail,
                                    ),
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {end < item.detailSerialNumber.length && (
                    <tr aria-hidden="true" style={{ height: (item.detailSerialNumber.length - end) * ROW_HEIGHT }}>
                      <td colSpan={columnCount} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>}
          </section>
        );
      })}
    </div>
  );
};

export default SerialRowTable;
